import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SubscriptionSection from '../components/SubscriptionSection';
import FounderSection from '../components/FounderSection';
import { EnterpriseSetupCTA } from '../components/marketing/EnterpriseSetupCTA';
import { RequestDemoForm } from '../components/marketing/RequestDemoForm';
import { IntroReel } from '../components/marketing/IntroReel';
import {
  Shield,
  FileText,
  GitBranch,
  CheckCircle2,
  Users,
  Settings,
  Database,
  ChevronRight,
  Award,
  FileCheck,
  Satellite,
  Rocket,
  Zap,
  Globe,
  Target,
  Building2,
  Brain,
  Cpu,
  Workflow,
  Layers,
  ArrowRight,
  Network,
  Factory,
  X,
  Sparkles,
  TrendingUp,
  BarChart3,
  Lock,
  RefreshCw,
  Activity
} from 'lucide-react';
import { LegalFooter, LegalBanner } from '../components/LegalNotice';

const HomePage = () => {
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
  const [expandedFeature, setExpandedFeature] = useState(null);

  // Check if user has already accepted terms in this session
  useEffect(() => {
    const accepted = sessionStorage.getItem('baselinx_terms_accepted');
    if (accepted === 'true') {
      setHasAcceptedTerms(true);
    }
  }, []);

  const handleAcceptTerms = () => {
    sessionStorage.setItem('baselinx_terms_accepted', 'true');
    setHasAcceptedTerms(true);
  };

  const handleDeclineTerms = () => {
    // Close the window or redirect
    window.close();
    // Fallback if window.close() doesn't work
    window.location.href = 'about:blank';
  };

  const features = [
    {
      icon: <Brain className="w-8 h-8" />,
      title: 'Configuration Intelligence Engine',
      description: 'AI-enabled analysis of configuration data with automated anomaly detection, impact prediction, and decision support across the system lifecycle.',
      link: '/cm-planning',
      details: {
        overview: 'The Configuration Intelligence Engine leverages advanced AI and machine learning to transform raw configuration data into actionable insights. It continuously monitors your configuration baseline, identifying anomalies, predicting impacts, and providing intelligent decision support.',
        capabilities: [
          { name: 'Anomaly Detection', desc: 'Automatically identifies configuration inconsistencies, duplicate parts, and potential conflicts before they impact operations' },
          { name: 'Impact Prediction', desc: 'Predicts downstream effects of proposed changes across assemblies, documentation, and supply chain' },
          { name: 'Decision Support', desc: 'AI-generated recommendations for change approval, risk mitigation, and schedule optimization' },
          { name: 'Trend Analysis', desc: 'Identifies patterns in configuration changes to predict future maintenance needs and resource requirements' }
        ],
        metrics: [
          { label: 'Anomaly Detection Rate', value: '99.2%' },
          { label: 'False Positive Rate', value: '<1%' },
          { label: 'Prediction Accuracy', value: '94%' }
        ]
      }
    },
    {
      icon: <Network className="w-8 h-8" />,
      title: 'MBSE Integration Layer',
      description: 'Seamlessly connect Model-Based Systems Engineering tools to execution systems. Bridge the gap between digital models and physical implementation.',
      link: '/configuration-identification',
      details: {
        overview: 'The MBSE Integration Layer creates a seamless bridge between Model-Based Systems Engineering environments and downstream execution systems. It ensures that digital models remain synchronized with physical implementation throughout the lifecycle.',
        capabilities: [
          { name: 'Model Synchronization', desc: 'Real-time sync between SysML/UML models and configuration baselines' },
          { name: 'Requirements Traceability', desc: 'Automated trace links from system requirements through design to verification' },
          { name: 'Interface Management', desc: 'Intelligent interface control document generation and validation' },
          { name: 'Digital Twin Connectivity', desc: 'Connect simulation models to as-built configuration data' }
        ],
        metrics: [
          { label: 'Model-to-Reality Sync', value: '100%' },
          { label: 'Trace Coverage', value: '98%' },
          { label: 'Integration Points', value: '15+' }
        ]
      }
    },
    {
      icon: <GitBranch className="w-8 h-8" />,
      title: 'Digital Thread Orchestration',
      description: 'Unified data flow from requirements through design, manufacturing, and sustainment. Complete traceability across the entire product lifecycle.',
      link: '/configuration-control',
      details: {
        overview: 'Digital Thread Orchestration provides end-to-end data continuity from concept through sustainment. It ensures that every piece of information flows seamlessly through the lifecycle, maintaining complete traceability and configuration integrity.',
        capabilities: [
          { name: 'Lifecycle Data Flow', desc: 'Automated data handoff between design, manufacturing, test, and sustainment phases' },
          { name: 'Baseline Management', desc: 'Intelligent baseline promotion with automated validation and approval workflows' },
          { name: 'As-Designed/As-Built/As-Maintained', desc: 'Complete visibility into configuration state at any point in the lifecycle' },
          { name: 'Change Propagation', desc: 'Automatic propagation of approved changes across all affected data domains' }
        ],
        metrics: [
          { label: 'Thread Completeness', value: '99%' },
          { label: 'Data Latency', value: '<1 min' },
          { label: 'Baseline Accuracy', value: '100%' }
        ]
      }
    },
    {
      icon: <Database className="w-8 h-8" />,
      title: 'Configuration Status Accounting',
      description: 'Real-time visibility into CI lifecycle state, revision history, change status, and effectivity with automated audit logs and compliance tracking.',
      link: '/status-accounting',
      details: {
        overview: 'Configuration Status Accounting provides comprehensive tracking and reporting of configuration item states throughout their lifecycle. Every change is recorded with complete audit trails for compliance and traceability.',
        capabilities: [
          { name: 'Real-Time Status', desc: 'Live dashboard showing current configuration state across all CIs' },
          { name: 'Revision History', desc: 'Complete revision timeline with reason codes and approval records' },
          { name: 'Effectivity Tracking', desc: 'Serial/lot effectivity management with automatic cutover point identification' },
          { name: 'Compliance Reporting', desc: 'Automated generation of status accounting reports per MIL-HDBK-61B/EIA-649' }
        ],
        metrics: [
          { label: 'Report Generation', value: '<5 sec' },
          { label: 'Audit Trail Depth', value: 'Unlimited' },
          { label: 'Compliance Coverage', value: '100%' }
        ]
      }
    },
    {
      icon: <CheckCircle2 className="w-8 h-8" />,
      title: 'Verification & Audit Automation',
      description: 'Automated FCA/PCA support with AI-assisted verification, discrepancy tracking, and audit-ready documentation generation.',
      link: '/audits',
      details: {
        overview: 'Verification & Audit Automation streamlines the configuration audit process with AI-assisted verification, automated discrepancy identification, and instant audit-ready documentation generation.',
        capabilities: [
          { name: 'FCA/PCA Automation', desc: 'Automated functional and physical configuration audit workflows with intelligent checklists' },
          { name: 'Discrepancy Detection', desc: 'AI-powered identification of discrepancies between documentation and physical articles' },
          { name: 'Evidence Collection', desc: 'Automated gathering and organization of audit evidence and artifacts' },
          { name: 'Corrective Action Tracking', desc: 'Integrated CAR/NCR management with closure verification' }
        ],
        metrics: [
          { label: 'Audit Time Reduction', value: '60%' },
          { label: 'Discrepancy Detection', value: '99%' },
          { label: 'First-Pass Success', value: '95%' }
        ]
      }
    },
    {
      icon: <Layers className="w-8 h-8" />,
      title: 'Cross-System Integration Hub',
      description: 'Native integration with PLM, ERP, MES, and CAD systems. Synchronize configuration data across your digital enterprise ecosystem.',
      link: '/plm-integration',
      details: {
        overview: 'The Cross-System Integration Hub provides native connectivity to enterprise systems, ensuring configuration data flows seamlessly across PLM, ERP, MES, and CAD platforms without manual intervention.',
        capabilities: [
          { name: 'PLM Integration', desc: 'Bi-directional sync with Teamcenter, Windchill, ENOVIA, Aras, and Arena' },
          { name: 'ERP Connectivity', desc: 'Real-time integration with SAP, Oracle, and Microsoft Dynamics' },
          { name: 'CAD System Bridge', desc: 'Direct integration with SolidWorks, CATIA, NX, Creo, and Altium' },
          { name: 'MES Synchronization', desc: 'Work order and routing sync with manufacturing execution systems' }
        ],
        metrics: [
          { label: 'Supported Systems', value: '25+' },
          { label: 'Data Accuracy', value: '99.9%' },
          { label: 'Sync Latency', value: '<30 sec' }
        ]
      }
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Defense-Grade Security',
      description: 'ITAR/CUI compliant with NIST 800-171 controls. Role-based access, data segregation, and comprehensive audit trails for defense programs.',
      link: '/digital-cm',
      details: {
        overview: 'Defense-Grade Security ensures your configuration data meets the strictest compliance requirements for defense and government programs, with built-in ITAR, CUI, and NIST 800-171 controls.',
        capabilities: [
          { name: 'ITAR Compliance', desc: 'Built-in controls for International Traffic in Arms Regulations with export marking support' },
          { name: 'CUI Protection', desc: 'Controlled Unclassified Information safeguarding per NIST 800-171' },
          { name: 'Role-Based Access', desc: 'Granular permissions with program-level data segregation' },
          { name: 'Audit Logging', desc: 'Immutable audit trails with tamper-evident logging and forensic support' }
        ],
        metrics: [
          { label: 'NIST Controls', value: '110/110' },
          { label: 'Uptime SLA', value: '99.99%' },
          { label: 'Audit Response', value: '<24 hrs' }
        ]
      }
    }
  ];

  const industries = [
    {
      icon: <Rocket className="w-12 h-12" />,
      title: 'Aerospace & Defense',
      description: 'Configuration management for military aircraft, defense systems, and aerospace programs',
      standards: 'ANSI/EIA-649, MIL-HDBK-61B',
      image: 'https://images.pexels.com/photos/34521/space-shuttle-lift-off-liftoff-nasa.jpg?auto=compress&cs=tinysrgb&w=800'
    },
    {
      icon: <Satellite className="w-12 h-12" />,
      title: 'Satellite Programs',
      description: 'Specialized CM for commercial and defense satellite programs with serialized hardware tracking',
      standards: 'Commercial & Defense Space',
      image: 'https://images.pexels.com/photos/586030/pexels-photo-586030.jpeg?auto=compress&cs=tinysrgb&w=800'
    }
  ];

  const stats = [
    { value: 'AI', label: 'Enabled Intelligence', icon: <Brain className="w-6 h-6" /> },
    { value: '100%', label: 'Digital Thread', icon: <Network className="w-6 h-6" /> },
    { value: 'DoD', label: 'DE Strategy Aligned', icon: <Shield className="w-6 h-6" /> },
    { value: '24/7', label: 'Mission Support', icon: <Globe className="w-6 h-6" /> }
  ];

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Legal Banner - Show before user accepts terms */}
      {!hasAcceptedTerms && (
        <LegalBanner 
          onAccept={handleAcceptTerms} 
          onDecline={handleDeclineTerms}
        />
      )}

      {/* Hero Section with Satellite Background */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden" data-testid="hero-section">
        {/* Background Image */}
        <div className="absolute inset-0">
          <IntroReel poster="https://images.pexels.com/photos/256379/pexels-photo-256379.jpeg?auto=compress&cs=tinysrgb&w=1920" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/85 to-slate-900/40 pointer-events-none" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent pointer-events-none" />
        </div>

        {/* Animated Grid Overlay */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: 'linear-gradient(rgba(6, 182, 212, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(6, 182, 212, 0.1) 1px, transparent 1px)',
            backgroundSize: '50px 50px'
          }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/20 border border-cyan-500/30 text-cyan-400 rounded-full text-sm font-semibold mb-6 animate-pulse-glow" data-testid="hero-badge">
              <Brain className="w-4 h-4" />
              AI-Enabled Digital Engineering Platform
            </div>
            
            <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6" data-testid="hero-title">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 animate-gradient">
                BASELINX
              </span>
              <br />
              <span className="text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl">Configuration Intelligence</span>
              <br />
              <span className="text-cyan-400 text-2xl sm:text-3xl md:text-4xl lg:text-5xl">for Defense Systems</span>
            </h1>
            
            <p className="text-base sm:text-xl text-gray-300 mb-4 font-space leading-relaxed" data-testid="hero-subtitle">
              The next-generation Digital Engineering platform that bridges MBSE and execution. 
              Orchestrate your digital thread from requirements through sustainment with AI-driven insights.
            </p>
            
            <p className="hidden sm:block text-lg text-cyan-400/80 mb-8 font-space">
              Aligned with DoD Digital Engineering Strategy • MBSE Integration • Lifecycle Optimization
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <Link 
                to="/demo" 
                className="group relative bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-8 py-4 rounded-lg font-bold text-lg overflow-hidden transition-all hover:shadow-lg hover:shadow-cyan-500/30"
                data-testid="hero-demo-button"
              >
                <span className="relative z-10 flex items-center justify-center gap-2">
                  <Rocket className="w-5 h-5" />
                  Launch Demo
                  <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition" />
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 opacity-0 group-hover:opacity-100 transition" />
              </Link>
              <Link 
                to="/plm-workflow" 
                className="border-2 border-cyan-500/50 text-cyan-400 px-8 py-4 rounded-lg font-bold text-lg hover:bg-cyan-500/10 hover:border-cyan-400 transition text-center"
                data-testid="hero-explore-button"
              >
                Explore Platform
              </Link>
            </div>

            {/* Stats Row */}
            <div className="grid grid-cols-4 gap-2 sm:gap-6 mt-6 sm:mt-0">
              {stats.map((stat, index) => (
                <div key={stat?.id ?? stat?.partNumber ?? stat?.ecoNumber ?? stat?.uuid ?? stat?.key ?? stat?.name ?? stat?.title ?? stat?.label ?? `idx-${index}`} className="text-center p-2 sm:p-4 bg-white/5 backdrop-blur-sm rounded-lg border border-white/10 card-hover">
                  <div className="flex justify-center text-cyan-400 mb-2">{stat.icon}</div>
                  <div className="font-heading text-base sm:text-2xl font-bold text-white">{stat.value}</div>
                  <div className="text-[10px] sm:text-sm leading-tight text-gray-400 font-space">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Floating Satellite Icon */}
        <div className="absolute right-10 top-1/3 hidden xl:block animate-float">
          <div className="w-24 h-24 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-full flex items-center justify-center border border-cyan-500/30 glow-cyan">
            <Network className="w-12 h-12 text-cyan-400" />
          </div>
        </div>
      </section>

      {/* Industries Section - Clean Two Tiles */}
      <section className="py-12 md:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-slate-900 to-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-white mb-4">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">
                Mission-Critical
              </span>
              {' '}Industries
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto font-space">
              Specialized configuration management for aerospace, defense, satellite, and commercial programs
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 m-swipe">
            {/* Defense & Aerospace Tile */}
            <Link 
              to="/defense-aerospace"
              className="group relative overflow-hidden rounded-2xl card-hover cursor-pointer"
              data-testid="defense-aerospace-tile"
            >
              <div className="absolute inset-0">
                <img 
                  src="https://images.pexels.com/photos/34521/space-shuttle-lift-off-liftoff-nasa.jpg?auto=compress&cs=tinysrgb&w=800"
                  alt="Aerospace & Defense"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/80 to-transparent" />
              </div>
              
              <div className="relative p-6 min-h-[320px] flex flex-col justify-end">
                <div className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center text-white mb-4 glow-cyan group-hover:scale-110 transition-transform">
                  <Rocket className="w-7 h-7" />
                </div>
                <h3 className="font-heading text-2xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">
                  Defense & Aerospace
                </h3>
                <p className="text-gray-300 mb-4 font-space text-sm">
                  Mission-critical CM for military aircraft, defense systems, and aerospace programs
                </p>
                <div className="flex flex-col gap-2">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-cyan-500/20 border border-cyan-500/30 text-cyan-400 rounded-full text-xs font-bold">
                    <CheckCircle2 className="w-3 h-3" />
                    AS9100 • MIL-STD-973 • EIA-649
                  </div>
                  <div className="flex items-center text-cyan-400 font-bold text-sm group-hover:translate-x-2 transition-transform">
                    Learn More <ChevronRight className="w-4 h-4 ml-1" />
                  </div>
                </div>
              </div>
            </Link>

            {/* Satellite Programs Tile */}
            <Link 
              to="/satellite-programs"
              className="group relative overflow-hidden rounded-2xl card-hover cursor-pointer"
            >
              <div className="absolute inset-0">
                <img 
                  src="https://images.pexels.com/photos/586030/pexels-photo-586030.jpeg?auto=compress&cs=tinysrgb&w=800"
                  alt="Satellite Programs"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/80 to-transparent" />
              </div>
              
              <div className="relative p-6 min-h-[320px] flex flex-col justify-end">
                <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center text-white mb-4 glow-cyan group-hover:scale-110 transition-transform">
                  <Satellite className="w-7 h-7" />
                </div>
                <h3 className="font-heading text-2xl font-bold text-white mb-2 group-hover:text-purple-400 transition">
                  Satellite & Space
                </h3>
                <p className="text-gray-300 mb-4 font-space text-sm">
                  Specialized CM for commercial and defense satellite programs with serialized tracking
                </p>
                <div className="flex flex-col gap-2">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-purple-500/20 border border-purple-500/30 text-purple-400 rounded-full text-xs font-bold">
                    <CheckCircle2 className="w-3 h-3" />
                    EIA-649 • ISO 9001 • NASA
                  </div>
                  <div className="flex items-center text-purple-400 font-bold text-sm group-hover:translate-x-2 transition-transform">
                    Learn More <ChevronRight className="w-4 h-4 ml-1" />
                  </div>
                </div>
              </div>
            </Link>

            {/* Commercial Programs Tile */}
            <Link 
              to="/commercial-programs"
              className="group relative overflow-hidden rounded-2xl card-hover cursor-pointer"
            >
              <div className="absolute inset-0">
                <img 
                  src="https://images.pexels.com/photos/3862634/pexels-photo-3862634.jpeg?auto=compress&cs=tinysrgb&w=800"
                  alt="Commercial Programs"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/80 to-transparent" />
              </div>
              
              <div className="relative p-6 min-h-[320px] flex flex-col justify-end">
                <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center text-white mb-4 glow-cyan group-hover:scale-110 transition-transform">
                  <Building2 className="w-7 h-7" />
                </div>
                <h3 className="font-heading text-2xl font-bold text-white mb-2 group-hover:text-emerald-400 transition">
                  Commercial Programs
                </h3>
                <p className="text-gray-300 mb-4 font-space text-sm">
                  Streamlined CM for commercial aviation, industrial, and consumer products
                </p>
                <div className="flex flex-col gap-2">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 rounded-full text-xs font-bold">
                    <CheckCircle2 className="w-3 h-3" />
                    ISO 9001 • AS9100 • APQP
                  </div>
                  <div className="flex items-center text-emerald-400 font-bold text-sm group-hover:translate-x-2 transition-transform">
                    Learn More <ChevronRight className="w-4 h-4 ml-1" />
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* NEW SECTION: Closing the Gap Between MBSE and Execution */}
      <section className="py-12 md:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-slate-800 to-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/20 border border-purple-500/30 text-purple-400 rounded-full text-sm font-semibold mb-6">
              <Workflow className="w-4 h-4" />
              The Digital Engineering Gap
            </div>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-white mb-6">
              Closing the Gap Between{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-cyan-400">
                MBSE and Execution
              </span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto font-space">
              Model-Based Systems Engineering defines the system digitally. BaseLinx ensures systems are 
              <span className="text-white font-semibold"> executed, configured, and sustained </span> 
              correctly throughout the lifecycle.
            </p>
          </div>

          {/* Visual Flow: Model → Configuration → Manufacturing → Sustainment */}
          <div className="relative mb-16">
            <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-purple-500 via-cyan-500 via-blue-500 to-green-500 transform -translate-y-1/2 opacity-30" />
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 m-swipe">
              {/* Model */}
              <div className="relative bg-slate-800/80 border border-purple-500/30 rounded-2xl p-6 hover:border-purple-500/60 transition group">
                <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-purple-500/30 transition">
                  <Cpu className="w-6 h-6 text-purple-400" />
                </div>
                <h3 className="font-heading text-xl font-bold text-white mb-2">Digital Model</h3>
                <p className="text-gray-400 text-sm font-space">MBSE defines system architecture, requirements, and interfaces in digital models</p>
                <div className="absolute -right-3 top-1/2 transform -translate-y-1/2 hidden lg:block">
                  <ArrowRight className="w-6 h-6 text-purple-400" />
                </div>
              </div>

              {/* Configuration */}
              <div className="relative bg-slate-800/80 border border-cyan-500/30 rounded-2xl p-6 hover:border-cyan-500/60 transition group">
                <div className="w-12 h-12 bg-cyan-500/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-cyan-500/30 transition">
                  <Settings className="w-6 h-6 text-cyan-400" />
                </div>
                <h3 className="font-heading text-xl font-bold text-white mb-2">Configuration</h3>
                <p className="text-gray-400 text-sm font-space">BaseLinx translates models into controlled configuration items with full traceability</p>
                <div className="absolute -right-3 top-1/2 transform -translate-y-1/2 hidden lg:block">
                  <ArrowRight className="w-6 h-6 text-cyan-400" />
                </div>
              </div>

              {/* Manufacturing */}
              <div className="relative bg-slate-800/80 border border-blue-500/30 rounded-2xl p-6 hover:border-blue-500/60 transition group">
                <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-blue-500/30 transition">
                  <Factory className="w-6 h-6 text-blue-400" />
                </div>
                <h3 className="font-heading text-xl font-bold text-white mb-2">Manufacturing</h3>
                <p className="text-gray-400 text-sm font-space">Configuration data flows to MES/ERP ensuring as-built matches as-designed</p>
                <div className="absolute -right-3 top-1/2 transform -translate-y-1/2 hidden lg:block">
                  <ArrowRight className="w-6 h-6 text-blue-400" />
                </div>
              </div>

              {/* Sustainment */}
              <div className="relative bg-slate-800/80 border border-green-500/30 rounded-2xl p-6 hover:border-green-500/60 transition group">
                <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-green-500/30 transition">
                  <Shield className="w-6 h-6 text-green-400" />
                </div>
                <h3 className="font-heading text-xl font-bold text-white mb-2">Sustainment</h3>
                <p className="text-gray-400 text-sm font-space">As-maintained configuration tracked through depot, upgrades, and end-of-life</p>
              </div>
            </div>
          </div>

          {/* The Gap Problem */}
          <div className="grid lg:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-red-500/10 to-orange-500/10 border border-red-500/30 rounded-2xl p-8">
              <h3 className="font-heading text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
                  <Target className="w-5 h-5 text-red-400" />
                </div>
                The Disconnect
              </h3>
              <p className="text-gray-300 mb-6 font-space">
                Organizations invest heavily in MBSE tools but struggle to connect digital models to real-world implementation. 
                The result: configuration drift, audit findings, and costly rework.
              </p>
              <ul className="space-y-3 text-sm">
                <li className="flex items-start gap-3 text-gray-300">
                  <div className="w-5 h-5 rounded-full bg-red-500/20 flex items-center justify-center shrink-0 mt-0.5">
                    <span className="text-red-400 text-xs">✗</span>
                  </div>
                  Models disconnected from configuration control
                </li>
                <li className="flex items-start gap-3 text-gray-300">
                  <div className="w-5 h-5 rounded-full bg-red-500/20 flex items-center justify-center shrink-0 mt-0.5">
                    <span className="text-red-400 text-xs">✗</span>
                  </div>
                  Manual handoffs between engineering and production
                </li>
                <li className="flex items-start gap-3 text-gray-300">
                  <div className="w-5 h-5 rounded-full bg-red-500/20 flex items-center justify-center shrink-0 mt-0.5">
                    <span className="text-red-400 text-xs">✗</span>
                  </div>
                  Configuration status unknown during sustainment
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-cyan-500/10 to-green-500/10 border border-cyan-500/30 rounded-2xl p-8">
              <h3 className="font-heading text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-cyan-400" />
                </div>
                The BaseLinx Solution
              </h3>
              <p className="text-gray-300 mb-6 font-space">
                BaseLinx serves as the configuration intelligence layer between MBSE and execution systems, 
                ensuring digital intent is preserved through physical implementation.
              </p>
              <ul className="space-y-3 text-sm">
                <li className="flex items-start gap-3 text-gray-300">
                  <div className="w-5 h-5 rounded-full bg-cyan-500/20 flex items-center justify-center shrink-0 mt-0.5">
                    <CheckCircle2 className="w-3 h-3 text-cyan-400" />
                  </div>
                  Automated model-to-configuration synchronization
                </li>
                <li className="flex items-start gap-3 text-gray-300">
                  <div className="w-5 h-5 rounded-full bg-cyan-500/20 flex items-center justify-center shrink-0 mt-0.5">
                    <CheckCircle2 className="w-3 h-3 text-cyan-400" />
                  </div>
                  Continuous digital thread from design through depot
                </li>
                <li className="flex items-start gap-3 text-gray-300">
                  <div className="w-5 h-5 rounded-full bg-cyan-500/20 flex items-center justify-center shrink-0 mt-0.5">
                    <CheckCircle2 className="w-3 h-3 text-cyan-400" />
                  </div>
                  AI-driven configuration intelligence and anomaly detection
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <EnterpriseSetupCTA />

      {/* Earth View Divider */}
      <section className="relative h-60 md:h-96 overflow-hidden">
        <img 
          src="https://images.pexels.com/photos/87651/earth-blue-planet-globe-planet-87651.jpeg?auto=compress&cs=tinysrgb&w=1920"
          alt="Earth from Space"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-800 via-transparent to-slate-900" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-white mb-4 text-glow">
              Digital Engineering at Scale
            </h2>
            <p className="text-xl text-cyan-300 font-space">Global Programs • Real-Time Intelligence • Mission-Critical Reliability</p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-12 md:py-24 px-4 sm:px-6 lg:px-8 bg-slate-900" data-testid="features-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/20 border border-cyan-500/30 text-cyan-400 rounded-full text-sm font-semibold mb-6">
              <Cpu className="w-4 h-4" />
              Platform Capabilities
            </div>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-white mb-4" data-testid="features-title">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-400">
                Configuration Intelligence
              </span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto font-space" data-testid="features-subtitle">
              AI-enabled capabilities that transform configuration data into strategic advantage
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 m-swipe">
            {features.map((feature, index) => (
              <div
                key={feature?.id ?? feature?.partNumber ?? feature?.ecoNumber ?? feature?.uuid ?? feature?.key ?? feature?.name ?? feature?.title ?? feature?.label ?? `idx-${index}`}
                onClick={() => setExpandedFeature(expandedFeature === index ? null : index)}
                className="group p-6 bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl hover:border-cyan-500/50 transition-all card-hover cursor-pointer"
                data-testid={`feature-card-${index}`}
              >
                <div className="w-16 h-16 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-xl flex items-center justify-center text-cyan-400 mb-4 group-hover:from-cyan-500 group-hover:to-blue-600 group-hover:text-white transition-all">
                  {feature.icon}
                </div>
                <h3 className="font-heading text-xl font-bold text-white mb-3 group-hover:text-cyan-400 transition">
                  {feature.title}
                </h3>
                <p className="text-gray-400 mb-4 font-space text-sm">
                  {feature.description}
                </p>
                <div className="flex items-center text-cyan-400 font-bold text-sm">
                  Explore Capability
                  <ChevronRight className={`ml-1 w-4 h-4 transition-transform ${expandedFeature === index ? 'rotate-90' : 'group-hover:translate-x-2'}`} />
                </div>
              </div>
            ))}
          </div>

          {/* Expanded Feature Detail Modal */}
          {expandedFeature !== null && features[expandedFeature]?.details && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={() => setExpandedFeature(null)}>
              <div 
                className="bg-slate-900 border border-cyan-500/30 rounded-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Modal Header */}
                <div className="bg-gradient-to-r from-cyan-500/20 to-purple-500/20 border-b border-cyan-500/30 p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center text-white">
                        {features[expandedFeature].icon}
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-white">{features[expandedFeature].title}</h2>
                        <p className="text-cyan-400/70 text-sm">Platform Capability</p>
                      </div>
                    </div>
                    <button 
                      onClick={() => setExpandedFeature(null)} 
                      className="p-2 hover:bg-white/10 rounded-lg transition"
                    >
                      <X className="w-6 h-6 text-gray-400" />
                    </button>
                  </div>
                </div>

                {/* Modal Content */}
                <div className="p-6 overflow-y-auto max-h-[calc(85vh-100px)]">
                  {/* Overview */}
                  <div className="mb-8">
                    <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-cyan-400" />
                      Overview
                    </h3>
                    <p className="text-gray-300 leading-relaxed">
                      {features[expandedFeature].details.overview}
                    </p>
                  </div>

                  {/* Key Metrics */}
                  <div className="grid grid-cols-3 gap-4 mb-8">
                    {features[expandedFeature].details.metrics.map((metric, i) => (
                      <div key={metric?.id ?? metric?.partNumber ?? metric?.ecoNumber ?? metric?.uuid ?? metric?.key ?? metric?.name ?? metric?.title ?? metric?.label ?? `idx-${i}`} className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 text-center">
                        <div className="text-2xl font-bold text-cyan-400 mb-1">{metric.value}</div>
                        <div className="text-xs text-gray-400">{metric.label}</div>
                      </div>
                    ))}
                  </div>

                  {/* Capabilities */}
                  <div>
                    <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                      <Activity className="w-5 h-5 text-purple-400" />
                      Key Capabilities
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      {features[expandedFeature].details.capabilities.map((cap, i) => (
                        <div key={cap?.id ?? cap?.partNumber ?? cap?.ecoNumber ?? cap?.uuid ?? cap?.key ?? cap?.name ?? cap?.title ?? cap?.label ?? `idx-${i}`} className="bg-slate-800/30 border border-slate-700/50 rounded-lg p-4 hover:border-cyan-500/30 transition">
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 bg-cyan-500/20 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
                              <CheckCircle2 className="w-4 h-4 text-cyan-400" />
                            </div>
                            <div>
                              <h4 className="text-white font-semibold mb-1">{cap.name}</h4>
                              <p className="text-gray-400 text-sm">{cap.desc}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Action Button */}
                  <div className="mt-8 flex justify-center">
                    <Link
                      to="/login"
                      className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-bold rounded-lg transition-all flex items-center gap-2"
                    >
                      Get Started with BaseLinx
                      <ArrowRight className="w-5 h-5" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Key Benefits Section */}
      <section className="py-12 md:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/20 border border-purple-500/30 text-purple-400 rounded-full text-sm font-semibold mb-6">
              <Award className="w-4 h-4" />
              Strategic Outcomes
            </div>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-white mb-4">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-400">
                Why Defense Leaders Choose BaseLinx
              </span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto font-space">
              Strategic value beyond traditional configuration management—enabling digital transformation at enterprise scale
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 m-swipe">
            {/* End-to-End Lifecycle */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 hover:border-cyan-500/50 transition group">
              <div className="w-14 h-14 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-xl flex items-center justify-center mb-4 group-hover:from-cyan-500/30 group-hover:to-blue-500/30 transition">
                <Target className="w-7 h-7 text-cyan-400" />
              </div>
              <h3 className="font-heading text-xl font-bold text-white mb-3">Lifecycle Optimization</h3>
              <p className="text-gray-400 text-sm mb-4 font-space">
                Maximize system availability and reduce total ownership cost through intelligent configuration management across the entire lifecycle.
              </p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2 text-gray-300">
                  <CheckCircle2 className="w-4 h-4 text-cyan-400 shrink-0" />
                  Concept → Design → Production → Sustainment
                </li>
                <li className="flex items-center gap-2 text-gray-300">
                  <CheckCircle2 className="w-4 h-4 text-cyan-400 shrink-0" />
                  As-Designed / As-Built / As-Maintained alignment
                </li>
              </ul>
            </div>

            {/* Single Source of Truth */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 hover:border-purple-500/50 transition group">
              <div className="w-14 h-14 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-xl flex items-center justify-center mb-4 group-hover:from-purple-500/30 group-hover:to-pink-500/30 transition">
                <Network className="w-7 h-7 text-purple-400" />
              </div>
              <h3 className="font-heading text-xl font-bold text-white mb-3">Authoritative Data Source</h3>
              <p className="text-gray-400 text-sm mb-4 font-space">
                Unified configuration intelligence that serves as the single source of truth across engineering, production, and sustainment.
              </p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2 text-gray-300">
                  <CheckCircle2 className="w-4 h-4 text-purple-400 shrink-0" />
                  Continuous digital thread connectivity
                </li>
                <li className="flex items-center gap-2 text-gray-300">
                  <CheckCircle2 className="w-4 h-4 text-purple-400 shrink-0" />
                  Real-time enterprise synchronization
                </li>
              </ul>
            </div>

            {/* BOM Management */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 hover:border-yellow-500/50 transition group">
              <div className="w-14 h-14 bg-gradient-to-br from-yellow-500/20 to-orange-500/20 rounded-xl flex items-center justify-center mb-4 group-hover:from-yellow-500/30 group-hover:to-orange-500/30 transition">
                <Layers className="w-7 h-7 text-yellow-400" />
              </div>
              <h3 className="font-heading text-xl font-bold text-white mb-3">BOM Intelligence</h3>
              <p className="text-gray-400 text-sm mb-4 font-space">
                Multi-view BOM with EBOM/MBOM transformation, supplier integration, and qualification tracking.
              </p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2 text-gray-300">
                  <CheckCircle2 className="w-4 h-4 text-yellow-400 shrink-0" />
                  EBOM → MBOM → SBOM transformation
                </li>
                <li className="flex items-center gap-2 text-gray-300">
                  <CheckCircle2 className="w-4 h-4 text-yellow-400 shrink-0" />
                  Parts qualification & cost roll-up
                </li>
              </ul>
            </div>

            {/* Quality & Compliance */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 hover:border-green-500/50 transition group">
              <div className="w-14 h-14 bg-gradient-to-br from-green-500/20 to-emerald-500/20 rounded-xl flex items-center justify-center mb-4 group-hover:from-green-500/30 group-hover:to-emerald-500/30 transition">
                <Shield className="w-7 h-7 text-green-400" />
              </div>
              <h3 className="font-heading text-xl font-bold text-white mb-3">Quality & Compliance</h3>
              <p className="text-gray-400 text-sm mb-4 font-space">
                Integrated QMS with regulatory compliance tracking, NCR/CAPA management, and audit readiness.
              </p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2 text-gray-300">
                  <CheckCircle2 className="w-4 h-4 text-green-400 shrink-0" />
                  AS9100 / ISO 9001 compliance
                </li>
                <li className="flex items-center gap-2 text-gray-300">
                  <CheckCircle2 className="w-4 h-4 text-green-400 shrink-0" />
                  Automated audit logs & reports
                </li>
              </ul>
            </div>

            {/* Traceability */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 hover:border-blue-500/50 transition group">
              <div className="w-14 h-14 bg-gradient-to-br from-blue-500/20 to-indigo-500/20 rounded-xl flex items-center justify-center mb-4 group-hover:from-blue-500/30 group-hover:to-indigo-500/30 transition">
                <GitBranch className="w-7 h-7 text-blue-400" />
              </div>
              <h3 className="font-heading text-xl font-bold text-white mb-3">Full Traceability</h3>
              <p className="text-gray-400 text-sm mb-4 font-space">
                Complete requirements-to-verification traceability with impact analysis and change history.
              </p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2 text-gray-300">
                  <CheckCircle2 className="w-4 h-4 text-blue-400 shrink-0" />
                  Requirements → Design → Test links
                </li>
                <li className="flex items-center gap-2 text-gray-300">
                  <CheckCircle2 className="w-4 h-4 text-blue-400 shrink-0" />
                  Serial/Lot tracking & genealogy
                </li>
              </ul>
            </div>

            {/* Collaboration */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 hover:border-pink-500/50 transition group">
              <div className="w-14 h-14 bg-gradient-to-br from-pink-500/20 to-rose-500/20 rounded-xl flex items-center justify-center mb-4 group-hover:from-pink-500/30 group-hover:to-rose-500/30 transition">
                <Users className="w-7 h-7 text-pink-400" />
              </div>
              <h3 className="font-heading text-xl font-bold text-white mb-3">Cross-Team Collaboration</h3>
              <p className="text-gray-400 text-sm mb-4 font-space">
                Secure supplier portal with role-based access, data governance, and external collaboration tools.
              </p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2 text-gray-300">
                  <CheckCircle2 className="w-4 h-4 text-pink-400 shrink-0" />
                  Supplier portal & ICD exchange
                </li>
                <li className="flex items-center gap-2 text-gray-300">
                  <CheckCircle2 className="w-4 h-4 text-pink-400 shrink-0" />
                  RBAC & data segregation
                </li>
              </ul>
            </div>
          </div>

          {/* Risk & Audit Row */}
          <div className="mt-12 grid md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/30 rounded-2xl p-8">
              <h3 className="font-heading text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <Award className="w-8 h-8 text-cyan-400" />
                Reduced Risk & Audit Readiness
              </h3>
              <p className="text-gray-300 mb-4 font-space">
                Proactive risk mitigation with automated compliance checks, deviation tracking, and always-ready audit documentation.
              </p>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex items-center gap-2 text-gray-300">
                  <CheckCircle2 className="w-4 h-4 text-cyan-400" />
                  Automated FCA/PCA support
                </div>
                <div className="flex items-center gap-2 text-gray-300">
                  <CheckCircle2 className="w-4 h-4 text-cyan-400" />
                  Real-time compliance dashboard
                </div>
                <div className="flex items-center gap-2 text-gray-300">
                  <CheckCircle2 className="w-4 h-4 text-cyan-400" />
                  Change impact analysis
                </div>
                <div className="flex items-center gap-2 text-gray-300">
                  <CheckCircle2 className="w-4 h-4 text-cyan-400" />
                  Deviation/waiver tracking
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-2xl p-8">
              <h3 className="font-heading text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <Globe className="w-8 h-8 text-purple-400" />
                Data Governance & Security
              </h3>
              <p className="text-gray-300 mb-4 font-space">
                Enterprise-grade security with ITAR/CUI compliance, role-based access control, and comprehensive audit trails.
              </p>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex items-center gap-2 text-gray-300">
                  <CheckCircle2 className="w-4 h-4 text-purple-400" />
                  ITAR & CUI data markings
                </div>
                <div className="flex items-center gap-2 text-gray-300">
                  <CheckCircle2 className="w-4 h-4 text-purple-400" />
                  Role-based access control
                </div>
                <div className="flex items-center gap-2 text-gray-300">
                  <CheckCircle2 className="w-4 h-4 text-purple-400" />
                  Complete audit history
                </div>
                <div className="flex items-center gap-2 text-gray-300">
                  <CheckCircle2 className="w-4 h-4 text-purple-400" />
                  Secure data segregation
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Digital Engineering Alignment Section */}
      <section className="py-12 md:py-24 px-4 sm:px-6 lg:px-8 bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/20 border border-blue-500/30 text-blue-400 rounded-full text-sm font-semibold mb-6">
              <Shield className="w-4 h-4" />
              DoD Digital Engineering Strategy
            </div>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-white mb-4">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
                Digital Engineering Alignment
              </span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto font-space">
              BaseLinx is architected to support DoD Digital Engineering objectives, MBSE adoption, and advanced manufacturing ecosystem integration
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12 m-swipe">
            <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-500/30 rounded-2xl p-6">
              <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center mb-4">
                <Cpu className="w-6 h-6 text-blue-400" />
              </div>
              <h3 className="font-heading text-xl font-bold text-white mb-3">MBSE Integration</h3>
              <p className="text-gray-400 text-sm font-space mb-4">
                Native integration with SysML/UML modeling tools. Synchronize system models with configuration baselines automatically.
              </p>
              <ul className="space-y-2 text-xs text-gray-300">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-3 h-3 text-blue-400" />
                  Cameo, MagicDraw, DOORS integration
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-3 h-3 text-blue-400" />
                  Model-to-CI traceability
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-2xl p-6">
              <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center mb-4">
                <Network className="w-6 h-6 text-purple-400" />
              </div>
              <h3 className="font-heading text-xl font-bold text-white mb-3">Digital Thread</h3>
              <p className="text-gray-400 text-sm font-space mb-4">
                Continuous data connectivity from requirements through sustainment. Enable data-driven decision making across the lifecycle.
              </p>
              <ul className="space-y-2 text-xs text-gray-300">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-3 h-3 text-purple-400" />
                  End-to-end traceability
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-3 h-3 text-purple-400" />
                  Cross-domain data federation
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-2xl p-6">
              <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center mb-4">
                <Factory className="w-6 h-6 text-green-400" />
              </div>
              <h3 className="font-heading text-xl font-bold text-white mb-3">Manufacturing Ecosystem</h3>
              <p className="text-gray-400 text-sm font-space mb-4">
                Connect configuration data to MES, ERP, and supply chain systems. Ensure as-built accuracy across the production network.
              </p>
              <ul className="space-y-2 text-xs text-gray-300">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-3 h-3 text-green-400" />
                  PLM/ERP/MES integration
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-3 h-3 text-green-400" />
                  Supplier collaboration portal
                </li>
              </ul>
            </div>
          </div>

          {/* Standards Compliance Bar */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6">
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm">
              <span className="text-gray-400 font-bold">Aligned With:</span>
              <span className="px-3 py-1.5 bg-blue-500/20 text-blue-400 rounded-lg font-mono">DoD DE Strategy</span>
              <span className="px-3 py-1.5 bg-purple-500/20 text-purple-400 rounded-lg font-mono">ANSI/EIA-649</span>
              <span className="px-3 py-1.5 bg-cyan-500/20 text-cyan-400 rounded-lg font-mono">MIL-HDBK-61B</span>
              <span className="px-3 py-1.5 bg-green-500/20 text-green-400 rounded-lg font-mono">NIST 800-171</span>
              <span className="px-3 py-1.5 bg-yellow-500/20 text-yellow-400 rounded-lg font-mono">AS9100</span>
            </div>
          </div>
        </div>
      </section>

      {/* Control Room Section */}
      <section className="relative py-12 md:py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/3582392/pexels-photo-3582392.jpeg?auto=compress&cs=tinysrgb&w=1920"
            alt="Control Room"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-3xl md:text-5xl font-bold text-white mb-6">
                <span className="text-cyan-400">Configuration Intelligence</span>
                <br />
                Command Center
              </h2>
              <p className="text-xl text-gray-300 mb-8 font-space">
                Real-time situational awareness across your entire program portfolio. AI-driven insights surface anomalies before they become audit findings.
              </p>
              <div className="space-y-4">
                {['AI-Powered Anomaly Detection', 'Predictive Compliance Analytics', 'Cross-Program Intelligence', 'Real-Time Decision Support'].map((item, index) => (
                  <div key={item?.id ?? item?.partNumber ?? item?.ecoNumber ?? item?.uuid ?? item?.key ?? item?.name ?? item?.title ?? item?.label ?? `idx-${index}`} className="flex items-center gap-3 text-gray-200">
                    <div className="w-8 h-8 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                      <CheckCircle2 className="w-5 h-5 text-cyan-400" />
                    </div>
                    <span className="font-space">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="bg-slate-800/80 backdrop-blur-sm border border-cyan-500/30 rounded-2xl p-6 glow-blue">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500" />
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                  <span className="ml-2 text-gray-400 text-sm font-mono">baselinx-intelligence</span>
                </div>
                <div className="font-mono text-sm space-y-2">
                  <p className="text-green-400">✓ Digital Thread: 100% connectivity</p>
                  <p className="text-cyan-400">→ AI Analysis: 3 optimization recommendations</p>
                  <p className="text-yellow-400">⚡ Configuration drift detected: ECO-4521</p>
                  <p className="text-green-400">✓ MBSE Sync: Models aligned with baselines</p>
                  <p className="text-purple-400">◉ Predictive: 98.5% compliance confidence</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <FounderSection />

      {/* Subscription Plans Section */}
      <SubscriptionSection />

      <RequestDemoForm />

      {/* CTA Section */}
      <section className="py-12 md:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-cyan-600 via-blue-600 to-purple-700">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm text-white rounded-full text-sm font-bold mb-6">
            <Brain className="w-4 h-4" />
            Experience Configuration Intelligence
          </div>
          <h2 className="font-heading text-3xl md:text-5xl font-bold text-white mb-6">
            See How AI Transforms Configuration Management
          </h2>
          <p className="text-xl text-blue-100 mb-8 font-space">
            Explore end-to-end digital engineering scenarios: MBSE integration, digital thread orchestration, AI-driven insights, and defense-grade compliance
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/demo-walkthrough" 
              className="group bg-white text-blue-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition flex items-center justify-center gap-2"
            >
              <Rocket className="w-5 h-5" />
              Launch Interactive Demo
              <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition" />
            </Link>
            <Link 
              to="/demo" 
              className="border-2 border-white text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white/10 transition"
            >
              Request Executive Briefing
            </Link>
          </div>
          <div className="mt-8 text-blue-200 text-sm font-space">
            Complete platform demonstration • AI capabilities showcase • No registration required
          </div>
        </div>
      </section>

      {/* Legal Footer */}
      <LegalFooter />
    </div>
  );
};

export default HomePage;
